import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * useToday — subscribe to /api/today for the logged-in user.
 *
 * Returns { data, loading, error, refresh }. Auto-fetches on mount and
 * whenever `deps` change. Includes tz_offset so the daypart/greeting on
 * the server reflects the user's local time.
 */
const useToday = (deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inflight = useRef(null);

  const fetchToday = useCallback(async () => {
    const token = localStorage.getItem('rbr_token');
    const tz = -new Date().getTimezoneOffset(); // minutes east of UTC
    // Cancel previous
    if (inflight.current) {
      try { inflight.current.abort(); } catch { /* ignore */ }
    }
    const ctrl = new AbortController();
    inflight.current = ctrl;
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/today?tz_offset=${tz}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: ctrl.signal,
      });
      if (!r.ok) throw new Error(`Today HTTP ${r.status}`);
      const json = await r.json();
      setData(json);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, refresh: fetchToday };
};

export default useToday;
